/**
 * AdminApiService
 * API service for admin session management operations
 * Feature: User Management (002-user-management) - Phase 3
 */

import ApiService, { API_BASE_URL } from './ApiService.js';

class AdminApiService {
  /**
   * Get sessions filtered by event and status
   * @param {number} eventId - Event ID
   * @param {string} status - Session status (draft/published/archived)
   * @returns {Promise<Array>} List of sessions
   */
  async getSessions(eventId, status = null, segment = null) {
    let endpoint = `/admin/sessions?eventId=${eventId}`;
    if (status) {
      endpoint += `&status=${status}`;
    }
    if (segment) {
      endpoint += `&segment=${encodeURIComponent(segment)}`;
    }
    return ApiService.get(endpoint);
  }

  /**
   * Get a single session by ID
   * @param {number} id - Session ID
   * @returns {Promise<Object>} Session object
   */
  async getSessionById(id) {
    return ApiService.get(`/admin/sessions/${id}`);
  }

  /**
   * Create a new session
   * @param {Object} sessionData - Session data
   * @returns {Promise<Object>} Created session
   */
  async createSession(sessionData) {
    return ApiService.post('/admin/sessions', sessionData);
  }

  /**
   * Update an existing session with conflict detection
   * @param {number} id - Session ID
   * @param {Object} updates - Fields to update
   * @param {string} clientUpdatedAt - Client's last known updatedAt timestamp
   * @returns {Promise<Object>} Updated session with optional conflict warning
   */
  async updateSession(id, updates, clientUpdatedAt = null) {
    const payload = { ...updates };
    if (clientUpdatedAt) {
      payload.clientUpdatedAt = clientUpdatedAt;
    }
    return ApiService.put(`/admin/sessions/${id}`, payload);
  }

  /**
   * Delete a session
   * @param {number} id - Session ID
   * @returns {Promise<Object>} Success message
   */
  async deleteSession(id) {
    return ApiService.delete(`/admin/sessions/${id}`);
  }

  /**
   * Publish a session
   * @param {number} id - Session ID
   * @returns {Promise<Object>} Published session
   */
  async publishSession(id) {
    return ApiService.post(`/admin/sessions/${id}/publish`, {});
  }

  /**
   * Unpublish a session (revert to draft)
   * @param {number} id - Session ID
   * @returns {Promise<Object>} Unpublished session
   */
  async unpublishSession(id) {
    return ApiService.post(`/admin/sessions/${id}/unpublish`, {});
  }

  /**
   * Get session statistics for admin's segment
   * @param {number} eventId - Optional event ID to filter
   * @returns {Promise<Object>} Session statistics
   */
  async getSessionStats(eventId = null, segment = null) {
    let endpoint = '/admin/sessions/stats';
    const params = [];
    if (eventId) params.push(`eventId=${eventId}`);
    if (segment) params.push(`segment=${encodeURIComponent(segment)}`);
    if (params.length > 0) endpoint += `?${params.join('&')}`;
    return ApiService.get(endpoint);
  }

  /**
   * Preview how sessions will appear on public dashboard
   * Phase 4 US3 - T088
   * @param {number} eventId - Event ID
   * @param {string} segment - Optional business segment
   * @returns {Promise<Array>} Preview data in public format
   */
  async previewSessions(eventId, segment = null) {
    let endpoint = `/admin/sessions/preview?eventId=${eventId}`;
    if (segment) endpoint += `&segment=${encodeURIComponent(segment)}`;
    return ApiService.get(endpoint);
  }

  /**
   * Bulk publish sessions for an event
   * Phase 4 US3 - T090
   * @param {number} eventId - Event ID
   * @param {Array<number>} sessionIds - Optional array of specific session IDs to publish
   * @param {string} segment - Optional business segment
   * @returns {Promise<Object>} Publish result with count and errors
   */
  async bulkPublishSessions(eventId, sessionIds = null, segment = null) {
    const payload = { eventId };
    if (sessionIds && sessionIds.length > 0) {
      payload.sessionIds = sessionIds;
    }
    if (segment) {
      payload.segment = segment;
    }
    return ApiService.post('/admin/sessions/publish', payload);
  }

  /**
   * Draft all sessions for an event (segment-scoped)
   * @param {number} eventId - Event ID
   * @param {string} segment - Optional business segment
   * @returns {Promise<Object>} Result with count
   */
  async draftAllSessions(eventId, segment = null) {
    return ApiService.post('/admin/sessions/draft-all', { eventId, segment });
  }

  /**
   * Delete all sessions for an event (segment-scoped)
   * @param {number} eventId - Event ID
   * @param {string} segment - Optional business segment
   * @returns {Promise<Object>} Result with count
   */
  async deleteAllSessions(eventId, segment = null) {
    return ApiService.post('/admin/sessions/delete-all', { eventId, segment });
  }

  /**
   * Upload Excel file for event
   * @param {File} file - Excel file
   * @param {string} segment - Business segment
   * @param {number} eventId - Event ID
   * @returns {Promise<Object>} Import result with counts and errors
   */
  async uploadExcelForEvent(file, segment, eventId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('segment', segment);

    const endpoint = `/admin/upload?eventId=${eventId}`;
    return ApiService.uploadExcel(file, segment, endpoint);
  }

  /**
   * Get all events (for event selector dropdown)
   * @param {string} filter - Filter by status (all/active/archived)
   * @returns {Promise<Array>} List of events
   */
  async getEvents(filter = 'all') {
    return ApiService.get(`/sessions/events?filter=${filter}`);
  }

  /**
   * Batch update sessions
   * @param {Array} sessions - Array of session updates {id, ...updates}
   * @returns {Promise<Object>} Batch update result
   */
  async batchUpdateSessions(sessions) {
    const promises = sessions.map(session => {
      const { id, clientUpdatedAt, ...updates } = session;
      return this.updateSession(id, updates, clientUpdatedAt);
    });
    
    const results = await Promise.allSettled(promises);
    
    const successful = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected');
    
    return {
      success: failed.length === 0,
      successful,
      failed: failed.length,
      errors: failed.map((r, idx) => ({
        sessionId: sessions[idx].id,
        error: r.reason?.message || 'Update failed'
      }))
    };
  }

  /**
   * Upload presenter picture for a specific session
   * @param {number} sessionId - Session ID
   * @param {File} file - Image file
   * @returns {Promise<Object>} Upload result with picture URL
   */
  /**
   * Delete presenter picture for a session
   * DELETE /api/admin/sessions/:sessionId/picture
   * @param {string} sessionId - Session ID
   * @param {string} pictureUrl - Optional specific picture URL to delete (for multi-presenter sessions)
   */
  async deletePresenterPicture(sessionId, pictureUrl = null) {
    try {
      let endpoint = `${API_BASE_URL}/admin/sessions/${sessionId}/picture`;
      if (pictureUrl) {
        endpoint += `?pictureUrl=${encodeURIComponent(pictureUrl)}`;
      }
      
      const response = await fetch(
        endpoint,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${ApiService.getAuthToken()}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete picture');
      }

      return await response.json();
    } catch (error) {
      console.error('Delete picture error:', error);
      throw error;
    }
  }

  /**
   * Upload presenter picture for a session
   * POST /api/admin/sessions/:sessionId/picture
   */
  async uploadPresenterPicture(sessionId, file) {
    const formData = new FormData();
    formData.append('picture', file);
    formData.append('sessionId', sessionId);

    console.log('Uploading picture:', {
      sessionId,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      url: `${API_BASE_URL}/admin/sessions/${sessionId}/picture`
    });

    try {
      const response = await fetch(`${API_BASE_URL}/admin/sessions/${sessionId}/picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${ApiService.getAuthToken()}`
        },
        body: formData
      });

      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
          console.error('Server error response:', errorData);
        } catch (parseError) {
          const textError = await response.text();
          console.error('Server error text:', textError);
          throw new Error(`HTTP ${response.status}: ${response.statusText} - ${textError}`);
        }
        
        const errorMessage = errorData.message || errorData.error || 'Failed to upload picture';
        const error = new Error(errorMessage);
        error.statusCode = response.status;
        error.serverResponse = errorData;
        error.details = errorData.details || errorData.stack;
        throw error;
      }

      const result = await response.json();
      console.log('Upload successful:', result);
      return result;
    } catch (error) {
      console.error('Upload picture error:', error);
      throw error;
    }
  }

  /**
   * Get feedback responses for admin's segment
   * @param {string} startDate - Optional start date filter (ISO format)
   * @param {string} endDate - Optional end date filter (ISO format)
   * @returns {Promise<Object>} Feedback responses with questions
   */
  async getFeedback(startDate = null, endDate = null) {
    let endpoint = '/admin/survey/feedback';
    const params = [];
    
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    
    if (params.length > 0) {
      endpoint += '?' + params.join('&');
    }
    
    return ApiService.get(endpoint);
  }

  // ── Survey Question Management ────────────────────────────────────────────

  /** Get all questions (incl. inactive) for admin's segment */
  async getSurveyQuestions() {
    return ApiService.get('/admin/survey/questions');
  }

  /** Create a new survey question */
  async createSurveyQuestion(questionText, questionType, options = null) {
    return ApiService.post('/admin/survey/questions', { questionText, questionType, options });
  }

  /** Update an existing survey question */
  async updateSurveyQuestion(id, updates) {
    return ApiService.put(`/admin/survey/questions/${id}`, updates);
  }

  /** Delete a survey question */
  async deleteSurveyQuestion(id) {
    return ApiService.delete(`/admin/survey/questions/${id}`);
  }

  // ── Category Management (super admin) ────────────────────────────────────
  async getCategories() {
    return ApiService.get('/super-admin/categories');
  }

  async createCategory(code, displayName, color) {
    return ApiService.post('/super-admin/categories', { code, displayName, color });
  }

  async updateCategory(code, updates) {
    return ApiService.put(`/super-admin/categories/${encodeURIComponent(code)}`, updates);
  }

  async deleteCategory(code) {
    return ApiService.delete(`/super-admin/categories/${encodeURIComponent(code)}`);
  }
}

export default new AdminApiService();
